import { Link, useLocation as useRouterLocation } from "react-router-dom";
import {
  ShoppingCart,
  MapPin,
  Search,
  User,
  Menu,
  Home,
  Package,
  Store,
  Gift,
  ChevronRight,
  Phone,
  HelpCircle,
  Loader2,
  LogOut,
  LayoutDashboard,
  ChevronDown,
  Globe,
  Check,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { useLocation } from "@/contexts/LocationContext";
import { useAuth, roleHomePath, type UserRole } from "@/contexts/AuthContext";
import { useLogout } from "@/hooks/useLogout";
import { getInitials } from "@/lib/account-profile-utils";
import { motion } from "framer-motion";
import { LocationSelectorModal } from "@/components/layout/LocationSelectorModal";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";

const navLinks = [
  { to: "/", label: "Home", icon: Home },
  { to: "/products", label: "Products", icon: Package },
  { to: "/stores", label: "Stores", icon: Store },
];

const languages = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
];

const secondaryLinks = [
  { to: "/account/team/direct", label: "Referral & Earn", icon: Gift },
  { to: "/help", label: "Help & Support", icon: HelpCircle },
  { to: "/contact", label: "Contact Us", icon: Phone },
];

function getRolePanelMeta(role: UserRole) {
  switch (role) {
    case "admin":
      return {
        dashboardLabel: "Admin Dashboard",
        profileLabel: "Admin Panel",
        profilePath: "/admin/profile",
        roleLabel: "Administrator",
      };
    case "store":
      return {
        dashboardLabel: "Retailer Dashboard",
        profileLabel: "Store Profile",
        profilePath: "/store/profile",
        roleLabel: "Retailer",
      };
    case "distributor":
      return {
        dashboardLabel: "Distributor Dashboard",
        profileLabel: "Distributor Panel",
        profilePath: "/distributor",
        roleLabel: "Distributor",
      };
    case "wholesaler":
      return {
        dashboardLabel: "Wholesaler Dashboard",
        profileLabel: "Wholesaler Panel",
        profilePath: "/wholesaler",
        roleLabel: "Wholesaler",
      };
    case "delivery":
      return {
        dashboardLabel: "Delivery Dashboard",
        profileLabel: "My Profile",
        profilePath: "/delivery/profile",
        roleLabel: "Delivery Partner",
      };
    case "customer":
      return {
        dashboardLabel: "My Account",
        profileLabel: "My Profile",
        profilePath: "/account/profile",
        roleLabel: "Customer",
      };
    default:
      return {
        dashboardLabel: "Dashboard",
        profileLabel: "Profile",
        profilePath: "/account/profile",
        roleLabel: "User",
      };
  }
}

const Header = () => {
  const { totalItems, setIsCartOpen } = useCart();
  const { selectedLocation, isDetecting } = useLocation();
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const logout = useLogout();
  const routerLocation = useRouterLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || "en";

  const isActive = (path: string) => routerLocation.pathname === path;

  const displayName = user?.name ?? "User";
  const displayEmail = user?.email ?? "";
  const initials = getInitials(displayName);
  const panelMeta = getRolePanelMeta(user?.role ?? null);
  const dashboardPath = roleHomePath(user?.role ?? null);

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    await logout();
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-border/50">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            className="h-14 w-14 p-0 shrink-0 text-foreground hover:bg-transparent hover:text-foreground focus:bg-transparent active:bg-transparent active:scale-95 transition-transform md:hidden"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-9 w-9 stroke-[2.5]" />
          </Button>

          <Link to="/" className="flex items-center gap-2">
            <img src="/icon.png" alt="Bihari Mall Icon" className="h-9 w-9 object-contain" />
            <img src="/nav-logo.png" alt="Bihari Mall" className="h-8 w-auto object-contain dark:invert" />
          </Link>

          <div 
            onClick={() => setLocationModalOpen(true)}
            className="hidden cursor-pointer items-center gap-2 rounded-full bg-secondary px-3 py-1.5 transition-colors hover:bg-secondary/80 md:flex"
          >
            {isDetecting ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            ) : (
              <MapPin className="h-4 w-4 text-primary" />
            )}
            <span className="text-sm font-medium text-foreground">{selectedLocation.label}</span>
            <span className="text-xs text-muted-foreground">▼</span>
          </div>

          <div className="mx-6 hidden max-w-md flex-1 lg:flex">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search products, stores..."
                className="h-10 w-full rounded-xl border-none bg-secondary pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground transition-all focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>

          <nav className="hidden items-center gap-1 md:flex">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg text-muted-foreground hover:text-foreground">
                  <Globe className="h-[18px] w-[18px]" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[150px] bg-popover border border-border shadow-xl z-50 rounded-xl p-1">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      localStorage.setItem("language", lang.code);
                    }}
                    className={`cursor-pointer gap-2 rounded-lg text-sm ${
                      currentLang === lang.code ? "bg-primary/10 text-primary font-medium" : ""
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span className="flex-1">{lang.label}</span>
                    {currentLang === lang.code && <Check className="h-3.5 w-3.5" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => setIsCartOpen(true)}
              aria-label="Open cart"
            >
              <ShoppingCart className="h-5 w-5" />
              {totalItems > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground"
                >
                  {totalItems}
                </motion.span>
              )}
            </Button>

            {authLoading ? (
              <Button variant="ghost" size="icon" disabled className="hidden md:flex">
                <Loader2 className="h-5 w-5 animate-spin" />
              </Button>
            ) : isAuthenticated && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="hidden h-9 gap-2 rounded-lg px-1.5 hover:bg-secondary/60 md:inline-flex sm:px-2.5"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-primary text-xs font-bold text-primary-foreground ring-2 ring-primary/20">
                      {initials}
                    </div>
                    <div className="hidden flex-col items-start leading-tight lg:flex">
                      <span className="max-w-[120px] truncate text-[13px] font-semibold text-foreground">
                        {displayName}
                      </span>
                      <span className="text-[10px] text-muted-foreground">{panelMeta.roleLabel}</span>
                    </div>
                    <ChevronDown className="hidden h-3 w-3 text-muted-foreground sm:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  sideOffset={8}
                  className="z-50 w-[240px] rounded-xl border border-border bg-popover p-1.5 shadow-2xl"
                >
                  <div className="mb-1 rounded-lg bg-secondary/40 px-2.5 py-3">
                    <p className="truncate text-sm font-bold text-foreground">{displayName}</p>
                    <p className="truncate text-[11px] text-muted-foreground">{displayEmail}</p>
                  </div>
                  <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg py-2 text-sm" asChild>
                    <Link to={dashboardPath}>
                      <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
                      {panelMeta.dashboardLabel}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg py-2 text-sm" asChild>
                    <Link to={panelMeta.profilePath}>
                      <User className="h-4 w-4 text-muted-foreground" />
                      {panelMeta.profileLabel}
                    </Link>
                  </DropdownMenuItem>
                  {user.role === "customer" && (
                    <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg py-2 text-sm" asChild>
                      <Link to="/account/orders">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        My Orders
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="my-1" />
                  <DropdownMenuItem
                    className="cursor-pointer gap-2.5 rounded-lg py-2 text-sm text-destructive focus:bg-destructive/10 focus:text-destructive"
                    onClick={() => logout()}
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" size="sm" className="hidden gap-2 md:inline-flex" asChild>
                <Link to="/auth/login">
                  <User className="h-4 w-4" />
                  <span className="hidden lg:inline">Sign In</span>
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-border/50 bg-card/60 px-4 py-2 backdrop-blur-sm md:hidden">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search products, stores..."
            className="h-9 w-full rounded-xl bg-secondary pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="flex w-[300px] flex-col p-0">
          <SheetHeader className="bg-gradient-primary p-5 pb-4 text-primary-foreground">
            {isAuthenticated && user ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-foreground/20 text-sm font-bold">
                    {initials}
                  </div>
                  <div className="min-w-0 text-left">
                    <SheetTitle className="truncate text-base text-primary-foreground">
                      {displayName}
                    </SheetTitle>
                    <SheetDescription className="truncate text-xs text-primary-foreground/75">
                      {displayEmail}
                    </SheetDescription>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
                    asChild
                  >
                    <Link to={dashboardPath} onClick={() => setMobileMenuOpen(false)}>
                      Dashboard
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
                    asChild
                  >
                    <Link to={panelMeta.profilePath} onClick={() => setMobileMenuOpen(false)}>
                      Profile
                    </Link>
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-foreground/20">
                    <User className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <div className="text-left">
                    <SheetTitle className="text-base text-primary-foreground">Welcome!</SheetTitle>
                    <SheetDescription className="text-xs text-primary-foreground/75">
                      Sign in for personalized deals
                    </SheetDescription>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 w-full border-primary-foreground/30 bg-primary-foreground/10 text-sm font-semibold text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
                  asChild
                >
                  <Link to="/auth/login" onClick={() => setMobileMenuOpen(false)}>
                    Sign In / Register
                  </Link>
                </Button>
              </>
            )}
          </SheetHeader>

          <div 
            onClick={() => {
              setLocationModalOpen(true);
              setMobileMenuOpen(false);
            }}
            className="flex cursor-pointer items-center gap-2 border-b border-border/50 bg-secondary/60 px-5 py-3 hover:bg-secondary transition-colors"
          >
            {isDetecting ? (
              <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-primary" />
            ) : (
              <MapPin className="h-4 w-4 flex-shrink-0 text-primary" />
            )}
            <span className="text-sm font-medium text-foreground">{selectedLocation.label}</span>
            <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
          </div>

          <nav className="flex-1 overflow-y-auto">
            <div className="py-2">
              <p className="px-5 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Shop
              </p>
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-5 py-3 text-sm font-medium transition-colors ${
                    isActive(link.to)
                      ? "border-r-[3px] border-primary bg-primary/10 text-primary"
                      : "text-foreground hover:bg-secondary"
                  }`}
                >
                  <link.icon
                    className={`h-5 w-5 ${isActive(link.to) ? "text-primary" : "text-muted-foreground"}`}
                  />
                  {link.label}
                  <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>

            <Separator />

            <div className="py-2">
              <p className="px-5 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                More
              </p>
              {secondaryLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-5 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
                >
                  <link.icon className="h-5 w-5 text-muted-foreground" />
                  {link.label}
                  <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>

            {isAuthenticated && user && (
              <>
                <Separator />
                <div className="py-2">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 px-5 py-3 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
                  >
                    <LogOut className="h-5 w-5" />
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </nav>

          <div className="border-t border-border/50 bg-secondary/40 p-4">
            <p className="text-center text-xs text-muted-foreground">
              Bihari Mall v1.0 · Made in India 🇮🇳
            </p>
          </div>
        </SheetContent>
      </Sheet>

      <LocationSelectorModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} />
    </header>
  );
};

export default Header;
